import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastGlobalService } from 'src/app/layout/toast-global/toast-global.service';
import { Actions } from 'src/app/models/actions';
import { VehicleMaintenance } from 'src/app/models/vehicleMaintenance';
import { VehicleSchedule } from 'src/app/models/vehicleSchedule';
import { VehicleDetailService } from '../vehicle-detail/vehicle-detail.service';
import { VehicleService } from '../vehicle/service/vehicle.service';
import { PartService } from 'src/app/fleet/part/service/part.service';
import { Part } from 'src/app/models/part';
import { TableFilter } from 'src/app/models/tableFilter';
import { PagedResponse } from 'src/app/models/pagedResponse';
import { TenantState } from 'src/app/store/state/tenant.state';
import { Tenant } from 'src/app/models/tenant';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { TenantStateService } from 'src/app/shared/services/tenant.state.service';
import { TranslateService } from '@ngx-translate/core';
import { VehicleState } from '../../models/vehicleState';
import { LocalizeService } from 'src/app/services/localize.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PartVehicle } from 'src/app/models/partVehicle';
import { DynamicDialogService } from 'src/app/dynamic-dialog/dynamic-dialog.service';
import { AuthService } from 'src/app/services/auth.service';
import { AppConfigurationService, AppSettings } from 'src/app/shared/services/app-configuration.service';
import { distinctUntilChanged, finalize, map, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { updateTabIndexRoute, getTabIndex } from 'src/app/shared/handler/common-functions.util';
import { VehicleMaintenanceRequest } from 'src/app/models/VehicleMaintenanceRequest';
import { SpaRoutingService } from 'src/app/shared/services/spa.routing.service';

@Component({
    selector: 'app-vehicle-maintenance-detail',
    templateUrl: './vehicle-maintenance-detail.component.html',
    styleUrls: ['./vehicle-maintenance-detail.component.css'],
    standalone: false
})
export class VehicleMaintenanceDetailComponent implements OnInit, OnDestroy {
  @SelectSnapshot(TenantState.getCurrentTenant) getCurrentTenant: Tenant;
  @SelectSnapshot(TenantState.getLanguage) getLanguage: string;
  vehicleMaintenance: VehicleMaintenance;
  vin: string;
  tenantId: string;
  @ViewChild('tabGroup') tabGroup;
  selectedIndex: any = 0;
  calculatedAvg = 0;

  Actions: typeof Actions = Actions;
  lamps: any;
  isNewRecord = false;
  vehicleState: VehicleState;
  maintenanceForm: UntypedFormGroup;
  vehicleSchedules: VehicleSchedule[];
  partsData: PagedResponse;
  lastFilter = new TableFilter();
  availLoading = false;
  loading: boolean = false;
  currentLanguage: string;

  length: number;
  innerWidth: number;
  direction = 'horizontal';
  assetUrl: string;
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private vehicleService: VehicleService,
    private vehicleDetailService: VehicleDetailService,
    private route: ActivatedRoute,
    private router: Router,
    private toastGlobalService: ToastGlobalService,
    private partService: PartService,
    private formBuilder: UntypedFormBuilder,
    private tenantStateService: TenantStateService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    private dialogService: DynamicDialogService,
    private localizeService: LocalizeService,
    private readonly appConfigurationService: AppConfigurationService,
    private spaRoutingService: SpaRoutingService,
      ) { }

  @HostListener('window:resize', ['$event'])
  onResize(event): void {
    this.innerWidth = window.innerWidth;
    this.direction = this.innerWidth >= 400 ? 'horizontal' : 'vertical';
  }

  ngOnInit(): void {
    this.currentLanguage = this.getLanguage;
    this.tenantId = this.getCurrentTenant.externalId;
    this.appConfigurationService.settings$
          .pipe(take(1))
          .subscribe((settings: AppSettings) => {
            this.assetUrl = this.appConfigurationService.settings.contentDeliveryNetworkUrl;
        });
    this.maintenanceForm = this.formBuilder.group({
      action: new UntypedFormControl(),
      updatedBy: new UntypedFormControl(),
      date: new UntypedFormControl(),
      selected: new UntypedFormControl(),
      notes: new UntypedFormControl(),
      odometer: new UntypedFormControl(),
      engineHours: new UntypedFormControl(),
      hours: new UntypedFormControl(),
    });
    if (location.search.includes('tabIndex')) {
      this.selectedIndex = getTabIndex(location.search);
    }
    if (location.search.includes('index')) {
      const urlParams = new URLSearchParams(window.location.search);
      this.selectedIndex = urlParams.get('index');
    }

    this.route.paramMap.pipe(
      map(params => ({ id: params.get('id'), vin: params.get('vin') })),
      distinctUntilChanged((a, b) => a.id === b.id && a.vin === b.vin),
      takeUntil(this.destroy$)
    ).subscribe(({ id, vin }) => {
      this.loadData(id, vin);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(id: string | null, vin: string | null): void {
    // selected from table
    if (this.vehicleService.selectedMaintenance && id === this.vehicleService.selectedMaintenance.id) {
      this.vehicleMaintenance = this.vehicleService.selectedMaintenance;
      this.loadScheduleAndInit();
    }
    // coming in from URL
    else if (id) {
      this.loading = true;
      this.vehicleService.getVehicleMaintenanceById(id)
        .pipe(finalize(() => this.loading = false))
        .subscribe(res => {
          this.vehicleMaintenance = res;
          this.loadScheduleAndInit();
        });
    }
    // coming in from plus button on filter list
    else if (vin) {
      this.vehicleMaintenance = new VehicleMaintenance();
      this.isNewRecord = true;
      this.vehicleMaintenance.vin = vin;
      this.vehicleDetailService.getVehicleStateByVin(vin)
        .subscribe(vehicleState => {
          this.vehicleMaintenance.vehicleState = vehicleState;
          this.setDisplayValues();
          this.reactiveForm();
        });
      this.vehicleService.getVehicleSchedule(vin, '{}', '{ "schedule.name": 1 }', 0, 400)
        .subscribe(res => {
          this.vehicleSchedules = res.documents;
        });
    }
  }

  onTabClick(event) {
    updateTabIndexRoute(event.index, this.router, this.route);
  }

  navigateToVehicle(): void {
    this.spaRoutingService.navigateToVehicle(
      this.route.snapshot.params.vin,
      { queryParams: { tabIndex: '5' } }
    );
  }

  setDisplayValues(): void {
    this.vehicleMaintenance.vehicleState.dashboard.indicators.forEach(lamp => {
      this.vehicleMaintenance.vehicleState.dashboard[lamp.name] = lamp.enabled;
    });
  }

  postVehicleMaintenance(mForm: any): void {
    if (mForm.invalid) {
      mForm.markAllAsTouched();
      const notification = this.translate.instant('@missingValues');
      const action = this.translate.instant('@gotItNow');
      this.snackBar.open(notification, action, { duration: 8555 });
      return;
    }

    if (this.isNewRecord) {
      this.vehicleMaintenance.vehicleScheduleId = mForm.get('scheduleId').value?.id;
      const foundSchedule = this.vehicleSchedules.find(z => z.id === this.vehicleMaintenance.vehicleScheduleId);
      this.vehicleMaintenance.vehicleScheduleName = foundSchedule.schedule.name;
      this.vehicleMaintenance.createdBy = this.authService.getUserProfile().name;
      this.vehicleMaintenance.action = mForm.get('action').value;
    }

    this.vehicleMaintenance.updatedBy = mForm.get('updatedBy').value?.trim();
    this.vehicleMaintenance.notes = mForm.get('notes').value?.trim();
    this.vehicleMaintenance.hours = mForm.get('hours').value;
    const currentSelected = mForm.get('selected').value;
    this.vehicleMaintenance.date[currentSelected] = mForm.get('date').value.toISOString();
    if (currentSelected === 'completeDate') {
      this.vehicleMaintenance.state.isComplete = true;
      this.vehicleMaintenance.completionDate = mForm.get('date').value.toISOString();
    }
    else {
      this.vehicleMaintenance.state.isComplete = false;
      this.vehicleMaintenance.completionDate = new Date('0001-01-01');
    }

    this.vehicleMaintenance.vehicleState.odometer =
      this.localizeService.setOdometer(
        this.currentLanguage,
        mForm.get('odometer').value);
    this.vehicleMaintenance.vehicleState.engine.operationDuration =
      this.localizeService.setHours(
        mForm.get('engineHours').value);

    if (this.isNewRecord) {
      this.vehicleService.createVehicleMaintenance(new VehicleMaintenanceRequest(this.vehicleMaintenance)).subscribe(res => {
        const toast = this.translate.instant('@updateSuccessful');
        this.toastGlobalService.show(toast, { classname: 'success' });
        this.navigateToVehicle();
      });
    } else {
      this.vehicleService.updateVehicleMaintenance(new VehicleMaintenanceRequest(this.vehicleMaintenance)).subscribe(res => {
        const toast = this.translate.instant('@updateSuccessful');
        this.toastGlobalService.show(toast, { classname: 'success' });
        this.navigateToVehicle();
      });
    }
  }

  reactiveForm(): void {
    let lastDate = this.vehicleMaintenance.date.updateDate;
    let currentSelected = 'updateDate';
    if (this.vehicleMaintenance.state.isComplete === 'true') {
      currentSelected = 'completeDate';
      lastDate = this.vehicleMaintenance.date.completeDate;
    }

    this.maintenanceForm = this.formBuilder.group({
      action: new UntypedFormControl({
        value: this.vehicleMaintenance.action ?? this.Actions[0], disabled: true
      }),
      updatedBy: [this.vehicleMaintenance.updatedBy ?? this.authService.getUserProfile().name, [Validators.required, Validators.pattern(/[\S]/)]],
      date: [lastDate ? new Date(lastDate) : (new Date()), Validators.required],
      selected: [currentSelected, Validators.required],
      notes: [this.vehicleMaintenance.notes ?? ''],
      hours: [this.vehicleMaintenance.hours ?? 0, Validators.required],
      odometer: [
        this.localizeService.getDisplayOdometer(
          this.currentLanguage,
          this.vehicleMaintenance.vehicleState?.odometer), Validators.required],
      engineHours: [
        this.localizeService.getHours(
          this.vehicleMaintenance.vehicleState?.engine?.operationDuration), Validators.required]
    });
  }

  updateData(passedFilter: TableFilter): void {
    if (this.vehicleMaintenance.id) {
      this.availLoading = true;
      this.partService.getVehicleMaintenanceData(
        this.vehicleMaintenance.id,
        passedFilter.filter,
        passedFilter.sort,
        passedFilter.offset,
        passedFilter.limit)
        .subscribe(res => {
          this.partsData = res.partResponse;
          this.lastFilter = passedFilter;
          this.availLoading = false;
        });
    }
  }

  updateSchedule(mForm: FormGroup): void {
    const vs = this.vehicleSchedules.find(z => z === mForm.get('scheduleId').value);
    this.maintenanceForm.controls.action.setValue(vs.schedule.action);

  }

  removeParts(event: any): void {
    if (event.parts) {
      this.vehicleMaintenance.parts = event.parts;
    }
    this.saveVehicle();
  }

  addPart(currentPart: Part): void {
    const payload = {
      part: currentPart,
      layout: null,
      showMassAdd: false
    };
    this.dialogService.openAddPartDialog(payload).afterClosed().subscribe(res => {
      if (res) {
        currentPart.location = res.location;
        currentPart.vehicles = new Array<PartVehicle>();
        const newPartVehicle = new PartVehicle();
        newPartVehicle.partQuantity = res.quantity;
        currentPart.vehicles.push(newPartVehicle);
        this.vehicleMaintenance.parts.push(currentPart);
        this.saveVehicle();
      }
    });
  }

  private loadScheduleAndInit(): void {
    if (this.vehicleMaintenance.vehicleScheduleId) {
      this.vehicleService.getVehicleScheduleById(
        this.vehicleMaintenance.vehicleScheduleId)
        .subscribe(res => {
          this.vehicleSchedules = [];
          this.vehicleSchedules.push(res);
          this.setDisplayValues();
          this.reactiveForm();
        });
    } else {
      this.setDisplayValues();
      this.reactiveForm();
    }
  }

  private saveVehicle(): void {
    this.vehicleService.updateVehicleMaintenance(new VehicleMaintenanceRequest(this.vehicleMaintenance)).subscribe(upd => {
      const toast = this.translate.instant('@updateSuccessful');
      this.toastGlobalService.show(toast, { classname: 'success' });
      this.updateData(this.lastFilter);
      this.vehicleService.getVehicleMaintenanceById(
        this.route.snapshot.params.id)
        .subscribe(res => {
          this.vehicleMaintenance = res;
          this.vehicleService.selectedMaintenance = res;
        });
    });

  }
}
